import { useEffect, useState } from "react";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioCard, RadioCardItem } from "@/components/ui/radio-group";
import { ListPlusIcon, PencilIcon, PlusCircleIcon } from "lucide-react";

type FieldType = "string" | "boolean" | "number" | "date";

interface AddCustomFieldDialogProps {
  onAdd: (name: string, description: string, type: FieldType, defaultValue?: number) => void;
  existingFieldNames: string[];
  disabled?: boolean;
  editMode?: boolean;
  editInitialValues?: {
    name: string;
    description: string;
    type: FieldType;
    defaultValue?: number;
  };
  onEdit?: (name: string, description: string, type: FieldType, defaultValue?: number) => void;
  externalOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  prefillValues?: { name: string; type: FieldType };
}

export default function AddCustomFieldDialog({
  onAdd,
  existingFieldNames,
  disabled,
  editMode,
  editInitialValues,
  onEdit,
  externalOpen,
  onOpenChange,
  prefillValues,
}: AddCustomFieldDialogProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = externalOpen ?? internalOpen;
  const setIsOpen = onOpenChange ?? setInternalOpen;

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [type, setType] = useState<FieldType>("string");
  const [defaultValue, setDefaultValue] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (isOpen && editMode && editInitialValues) {
      setName(editInitialValues.name);
      setDescription(editInitialValues.description);
      setType(editInitialValues.type);
      setDefaultValue(editInitialValues.defaultValue?.toString() ?? "");
      setError("");
    } else if (isOpen && !editMode && prefillValues) {
      setName(prefillValues.name);
      setType(prefillValues.type);
      setDescription("");
      setDefaultValue("");
      setError("");
    }
  }, [isOpen, editMode, prefillValues?.name]);

  const resetForm = () => {
    setName("");
    setDescription("");
    setType("string");
    setDefaultValue("");
    setError("");
  };

  const handleSubmit = () => {
    if (!name.trim()) {
      setError("Field name is required");
      return;
    }

    if (!description.trim()) {
      setError("Field description is required");
      return;
    }

    // Skip own name when checking uniqueness in edit mode
    const namesToCheck = editMode && editInitialValues
      ? existingFieldNames.filter((n) => n !== editInitialValues.name)
      : existingFieldNames;

    if (namesToCheck.includes(name.trim())) {
      setError("Field name must be unique");
      return;
    }

    if (editMode && onEdit) {
      onEdit(name.trim(), description.trim(), type, defaultValue ? Number(defaultValue) : undefined);
    } else {
      onAdd(name.trim(), description.trim(), type, defaultValue ? Number(defaultValue) : undefined);
    }

    resetForm();
    setIsOpen(false);
  };

  const handleCancel = () => {
    resetForm();
    setIsOpen(false);
  };

  return (
    <AlertDialog open={isOpen} onOpenChange={setIsOpen}>
      {!editMode && externalOpen === undefined && (
        <AlertDialogTrigger asChild>
          <Button
            variant="outline"
            disabled={disabled}
            className="w-full h-16 border-dashed text-muted-foreground hover:text-muted-foreground hover:bg-sidebar group"
          >
            <PlusCircleIcon className="group-hover:rotate-180 transition-all duration-600" />
            Add Custom Field
          </Button>
        </AlertDialogTrigger>
      )}
      <AlertDialogContent className="max-w-xl">
        <AlertDialogHeader>
          <AlertDialogTitle>{editMode ? "Edit Custom Field" : "Add Custom Field"}</AlertDialogTitle>
          <AlertDialogDescription>
            {editMode
              ? "Update this custom field's properties."
              : "Create a new custom field to collect specific information from creators."}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-4 ">
          <div className="space-y-2">
            <Label htmlFor="field-name">Field Name *</Label>
            <Input
              id="field-name"
              placeholder="e.g. Has a Dog"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setError("");
              }}
              autoFocus
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="field-description">Description</Label>
            <Textarea
              id="field-description"
              placeholder="Explain briefly what information you want to collect with this field."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="min-h-20 max-w-133.5"
            />
          </div>

          <div className="space-y-2">
            <Label>Field Type</Label>
            <RadioGroup
              value={type}
              onValueChange={(v) => setType(v as FieldType)}
              className="flex gap-3"
            >
              {[
                { value: "string", label: "Text" },
                { value: "boolean", label: "Yes/No" },
                { value: "number", label: "Number" },
                { value: "date", label: "Date" },
              ].map((opt) => (
                <RadioCard
                  key={opt.value}
                  className="items-center gap-2 w-auto rounded-md px-3 py-2 cursor-pointer"
                >
                  <RadioCardItem
                    value={opt.value}
                    id={`type-${opt.value}`}
                    className="order-0 ml-0"
                  />
                  <Label htmlFor={`type-${opt.value}`} className="cursor-pointer text-sm">
                    {opt.label}
                  </Label>
                </RadioCard>
              ))}
            </RadioGroup>
            {type === "number" && (
              <>
                <p className="text-muted-foreground text-xs">
                  Amy will ask the creator for a numeric value. Use this field's name as a variable in a custom formula.
                </p>
                <div className="space-y-2 mt-3">
                  <Label htmlFor="field-default-value">Default Value (Optional)</Label>
                  <Input
                    id="field-default-value"
                    placeholder="e.g. 50000"
                    type="text"
                    value={defaultValue}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^\d.]/g, "");
                      setDefaultValue(value);
                    }}
                  />
                  <p className="text-muted-foreground text-xs">
                    If set, this value will be used in formula calculations until Amy collects the actual value from the creator.
                  </p>
                </div>
              </>
            )}
            {type === "date" && (
              <p className="text-muted-foreground text-xs">
                Amy will ask the creator for a date (e.g., posting date, deadline). You can use this field to trigger stages.
              </p>
            )}
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>

        <AlertDialogFooter>
          <Button variant="secondary" onClick={handleCancel}>
            Cancel
          </Button>
          <Button onClick={handleSubmit}>
            {editMode ? <PencilIcon /> : <ListPlusIcon />}
            {editMode ? "Save Changes" : "Add Field"}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
